import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Search, Plus, Edit, ToggleLeft, ToggleRight, X } from "lucide-react";
import Layout from "./layout";
import CreateTemplateModal from "../components/creatTemplate";
import EditTemplateModal from "../components/editTemplate";
import { templateRecordsData, activityLogData } from "../data/mockData";
import { templateAPI, activityLogsAPI, tagsAPI } from "../services/api";

const PageContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 400;
  color: #111827;
  margin-bottom: 2rem;
`;

const FilterSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr auto auto;
  grid-template-rows: auto auto auto;
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: end;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #000000;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #CFD3D4;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: border-color 0.2s;
  width: 100%;

  &::placeholder {
    color: #9CA3AF;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: #FFFFFF;
  cursor: pointer;
  transition: border-color 0.2s;
  width: 100%;
`;

const SearchButton = styled.button`
  background-color: #00B5B8;
  color: #FFFFFF;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  height: fit-content;
  width: 100px;
  min-width: 100px;

  &:hover {
    background-color: #0891B2;
  }
`;

const AddButton = styled.button`
  background-color: #FBFFFF;
  color: #9c9e9eff;
  border: #CFD3D4 solid 1px;
  border-radius: 0.5rem;
  padding: 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  height: fit-content;
  width: 100px;
  min-width: 100px;
`;

const FilterTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const FilterTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  background-color: transparent;
  border: 1px solid #00B5B8;
  color: #00B5B8;
  border-radius: 0px;
  padding: 0.25rem 0.5rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  height: 30px;
  width: auto;
  position: relative;
  cursor: pointer;
`;

const CloseIcon = styled.button`
  background: white;
  border: 1px solid #00B5B8;
  cursor: pointer;
  color: #00B5B8;
  padding: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: absolute;
  top: -8px;
  right: -8px;
`;

const AutocompleteWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #D1D5DB;
  border-top: none;
  border-radius: 0 0 0.375rem 0.375rem;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const SuggestionItem = styled.div`
  padding: 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
  transition: background-color 0.2s;

  &:hover {
    background-color: #F3F4F6;
  }

  &:last-child {
    border-radius: 0 0 0.375rem 0.375rem;
  }
`;

const AddRecordButton = styled.button`
  background-color: #FBFFFF;
  color: #9c9e9eff;
  border: #CFD3D4 solid 1px;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #059669;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  border: 1px solid #E5E7EB;
  border-radius: 0.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #F9FAFB;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #E5E7EB;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #F9FAFB;
  }

  &[data-sub-row="true"] {
    background-color: inherit;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 0.875rem;
  color: #374151;
  vertical-align: top;
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6F6F6F;
  text-align: left;
`;

const TableHeaderCell2 = styled.th`
  padding: 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #6F6F6F;
  text-align: left;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => props.status === 'completed' ? '#D1FAE5' : 
                                props.status === 'pending' ? '#FEF3C7' : '#FEE2E2'};
  color: ${props => props.status === 'completed' ? '#065F46' : 
                    props.status === 'pending' ? '#92400E' : '#991B1B'};
`;

const EditButton = styled.button`
  background-color: #F3F4F6;
  color: #374151;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #E5E7EB;
  }
`;

const ToggleSwitch = styled.div`
  width: 44px;
  height: 24px;
  background-color: ${props => props.active ? '#00B5B8' : '#E5E7EA'};
  border-radius: 12px;
  position: relative;
  cursor: pointer;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
`;

const ToggleCircle = styled.div`
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 50%;
  position: absolute;
  left: ${props => props.active ? '22px' : '2px'};
  transition: left 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const GridItem = styled.div`
  grid-column: ${props => props.column};
  grid-row: ${props => props.row};
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #E5E7EB;
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: #6B7280;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const PageButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid #D1D5DB;
  background-color: ${props => props.active ? '#00B5B8' : '#FFFFFF'};
  color: ${props => props.active ? '#FFFFFF' : '#374151'};
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  min-width: 40px;

  &:hover:not(:disabled) {
    background-color: ${props => props.active ? '#0891B2' : '#F9FAFB'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageEllipsis = styled.span`
  padding: 0.5rem;
  color: #6B7280;
  font-size: 0.875rem;
`;

const SubTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem;
  margin: 0;
`;

const SubTableRow = styled.tr`
  border-bottom: 1px solid #E5E7EB;

  &:last-child {
    border-bottom: none;
  }
`;

const SubTableCell = styled.td`
  padding: 0.4rem 0.3rem;
  font-size: 0.7rem;
  color: #374151;
  text-align: center;
  vertical-align: top;
`;

const SubTableHeaderCell = styled.th`
  padding: 0.4rem 0.3rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #6B7280;
  text-align: center;
  background-color: #F9FAFB;
`;

const HealthBook = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [filteredTagSuggestions, setFilteredTagSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [healthRecords, setHealthRecords] = useState([]);
  const [allHealthRecords, setAllHealthRecords] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const isRadioParameter = (parameterName) => {
    const radioParams = [
      'Urine Appearance',
      'Color', 'Clarity',
      'Color (Stool)', 'Appearance (Stool)', 'Parasite',
      'RBC Index',
      'HBsAb', 'HBsAg', 'HCV Ab', 'Hemoglobin detection stool',
    ];
    return radioParams.includes(parameterName);
  };

  const isCountDropdownParameter = (parameterName) => {
    return ['WBC (Stool)', 'RBC (Stool)'].includes(parameterName);
  };

  const renderCustomValue = (param) => {
    if (isRadioParameter(param.parameter_name)) {
      return param.custom_value || '-';
    }
    if (isCountDropdownParameter(param.parameter_name)) {
      const min = param.custom_min;
      const max = param.custom_max;
      if (!min && !max) {
        return param.custom_value || '-';
      }
      if (min === max) return String(min);
      return `${min}-${max}`;
    }
    const min = param.custom_min;
    const max = param.custom_max;
    if (!min && !max) return '-';
    if (min === max) return String(min);
    return `${min}-${max}`;
  };

  useEffect(() => {
    loadTemplates();
    loadActivityLogs();
    loadAvailableTags();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [activityLog.length]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const loadAvailableTags = async () => {
    try {
      const response = await tagsAPI.getUsedTags();

      if (response.status === 'success' && response.data && Array.isArray(response.data)) {
        const tagNames = response.data.map(tag => tag.tag_name);
        setAvailableTags(tagNames);
      }
    } catch (error) {
      console.error('❌ Error loading tags:', error);
      setAvailableTags([]);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await templateAPI.getAll();

      if (response.status === 'success' && response.data) {
        const formattedData = response.data.map((template, index) => ({
          id: template.id,
          no: index + 1,
          templateName: template.template_name,
          createdBy: template.created_by,
          tags: template.tags || [],
          gender: template.gender === 'male' ? 'ชาย' : template.gender === 'female' ? 'หญิง' : template.gender === 'both' ? 'ทั้งสองเพศ' : 'ทั้งสองเพศ',
          ageRange: (template.age_min !== null && template.age_max !== null)
            ? `${parseFloat(template.age_min)}-${parseFloat(template.age_max)}`
            : '-',
          parametersData: template.parameters || [],
          isActive: Boolean(template.is_active)
        }));

        setHealthRecords(formattedData);
        setAllHealthRecords(formattedData);

      } else {
        setHealthRecords(templateRecordsData);
      }
    } catch (error) {
      console.error('❌ Error loading templates:', error);
      setHealthRecords(templateRecordsData);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityLogs = async () => {
    try {
      const response = await activityLogsAPI.getAll();

      if (response.status === 'success' && response.data) {
        setActivityLog(response.data);
      } else {
        setActivityLog(activityLogData);
      }
    } catch (error) {
      console.error('❌ Error loading activity logs:', error);
      setActivityLog(activityLogData);
    }
  };

  const handleSearch = () => {
    let filtered = allHealthRecords;

    if (searchTerm.trim()) {
      filtered = filtered.filter(record =>
        record.templateName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedDoctor) {
      filtered = filtered.filter(record =>
        record.createdBy && record.createdBy.includes(selectedDoctor)
      );
    }

    if (selectedGender) {
      const genderMap = {
        'male': 'ชาย',
        'female': 'หญิง',
        'both': 'ทั้งสองเพศ'
      };
      const selectedGenderThai = genderMap[selectedGender];

      filtered = filtered.filter(record =>
        record.gender === selectedGenderThai
      );
    }

    if (selectedTags.length > 0) {
      filtered = filtered.filter(record =>
        selectedTags.some(selectedTag =>
          record.tags && record.tags.includes(selectedTag)
        )
      );
    }

    setHealthRecords(filtered);
  };

  const removeSelectedTag = (tagToRemove) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
  };

  const handleTagInputChange = (value) => {
    setTagInput(value);

    if (value.trim()) {
      const filtered = availableTags.filter(tag =>
        tag.toLowerCase().includes(value.toLowerCase()) &&
        !selectedTags.includes(tag)
      );
      console.log('🔍 ค้นหา:', value, '| Tags ทั้งหมด:', availableTags.length, '| ผลลัพธ์:', filtered);
      setFilteredTagSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (tag) => {
    if (!selectedTags.includes(tag)) {
      setSelectedTags([...selectedTags, tag]);
    }
    setTagInput("");
    setShowSuggestions(false);
  };

  const addCustomTag = () => {
    if (tagInput.trim() && !selectedTags.includes(tagInput.trim())) {
      setSelectedTags([...selectedTags, tagInput.trim()]);
      setTagInput("");
      setShowSuggestions(false);
    }
  };

  const editRecord = (id) => {
  };

  const addRecord = () => {
    setIsModalOpen(true);
  };

  const handleSaveTemplate = async (templateData) => {
    try {
      setLoading(true);

      const response = await templateAPI.create(templateData);

      if (response.status === 'success' || response.status === 'created') {
        await loadTemplates();
        await loadActivityLogs();

        setIsModalOpen(false);
      } else {
        throw new Error(response.message || 'Failed to create template');
      }
    } catch (error) {
      console.error('❌ Error saving template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleEditTemplate = async (templateData) => {
    try {
      setLoading(true);

      const response = await templateAPI.update(editingTemplate.id, templateData);

      if (response.status === 'success') {
        await loadTemplates();
        await loadActivityLogs();

        setIsEditModalOpen(false);
        setEditingTemplate(null);
      } else {
        throw new Error(response.message || 'Failed to update template');
      }
    } catch (error) {
      console.error('❌ Error updating template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingTemplate(null);
  };

  const totalPages = Math.ceil(activityLog.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentActivityLogs = activityLog.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };


  return (
    <Layout>
      <PageContainer>
        <PageTitle>Template การแปลผลสุขภาพ</PageTitle>

        <FilterSection>
          <GridItem column="1" row="1">
            <Label>ค้นหาชื่อ Template</Label>
          </GridItem>
          <GridItem column="2" row="1">
            <Label>ค้นหาจากผู้สร้าง Template</Label>
          </GridItem>
          <GridItem column="3" row="1">
            <Label>สำหรับเพศ</Label>
          </GridItem>
          <GridItem column="4" row="1">
            <Label>ป้ายกำกับ Tag</Label>
          </GridItem>

          <GridItem column="1" row="2">
            <Input 
              type="text" 
              placeholder="พิมพ์ชื่อค้นหา"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </GridItem>
          
          <GridItem column="2" row="2">
            <Select
              value={selectedDoctor}
              onChange={(e) => setSelectedDoctor(e.target.value)}
            >
              <option value="">เลือก</option>
              {[...new Set(allHealthRecords.map(t => t.createdBy).filter(Boolean))].map((creator, index) => (
                <option key={index} value={creator}>{creator}</option>
              ))}
            </Select>
          </GridItem>
          
          <GridItem column="3" row="2">
            <Select 
              value={selectedGender}
              onChange={(e) => setSelectedGender(e.target.value)}
            >
              <option value="">เลือก</option>
              <option value="male">เพศชาย</option>
              <option value="female">เพศหญิง</option>
              <option value="both">ทั้งสองเพศ</option>
            </Select>
          </GridItem>
          
          <GridItem column="4" row="2">
            <AutocompleteWrapper>
              <Input
                type="text"
                placeholder="พิมพ์ tag ใหม่"
                value={tagInput}
                onChange={(e) => handleTagInputChange(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addCustomTag();
                  }
                }}
              />
              {showSuggestions && filteredTagSuggestions.length > 0 && (
                <SuggestionsDropdown>
                  {filteredTagSuggestions.map((tag, index) => (
                    <SuggestionItem
                      key={index}
                      onClick={() => selectSuggestion(tag)}
                    >
                      {tag}
                    </SuggestionItem>
                  ))}
                </SuggestionsDropdown>
              )}
            </AutocompleteWrapper>
          </GridItem>

          <GridItem column="5" row="2">
            <AddButton onClick={addCustomTag}>
              เพิ่ม
            </AddButton>
          </GridItem>
          
          <GridItem column="6" row="2">
            <SearchButton onClick={handleSearch}>
              ค้นหา
            </SearchButton>
          </GridItem>
          
          <GridItem column="4" row="3">
            {selectedTags.length > 0 && (
              <FilterTags>
                {selectedTags.map((tag, index) => (
                  <FilterTag key={index}>
                    <span>{tag}</span>
                    <CloseIcon onClick={() => removeSelectedTag(tag)}>
                      <X size={8} />
                    </CloseIcon>
                  </FilterTag>
                ))}
              </FilterTags>
            )}
          </GridItem>

          <GridItem column="6" row="3">
            <AddRecordButton onClick={addRecord}>
              <Plus size={16} />
              สร้าง Template
            </AddRecordButton>
          </GridItem>
        </FilterSection>

        <TableContainer>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell rowSpan="2">ที่</TableHeaderCell>
                <TableHeaderCell rowSpan="2">Template</TableHeaderCell>
                <TableHeaderCell rowSpan="2">สร้างโดย</TableHeaderCell>
                <TableHeaderCell rowSpan="2">ป้ายกำกับ</TableHeaderCell>
                <TableHeaderCell rowSpan="2">สำหรับเพศ</TableHeaderCell>
                <TableHeaderCell rowSpan="2">ช่วงอายุ (ปี)</TableHeaderCell>
                <TableHeaderCell colSpan="3" style={{ textAlign: 'center' }}>ผลตรวจสุขภาพ</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ textAlign: 'center' }}>แก้ไข</TableHeaderCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell2>พารามิเตอร์</TableHeaderCell2>
                <TableHeaderCell2>ค่าปกติ</TableHeaderCell2>
                <TableHeaderCell2>ค่าที่กำหนด</TableHeaderCell2>
              </TableRow>
            </TableHeader>
            <tbody>
              {loading && healthRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="10" style={{ textAlign: 'center', padding: '2rem' }}>
                    กำลังโหลดข้อมูล...
                  </TableCell>
                </TableRow>
              ) : healthRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="10" style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
                    ไม่มี Template
                  </TableCell>
                </TableRow>
              ) : (
                healthRecords.map((record) => (
                <React.Fragment key={record.id}>
                <TableRow>
                  <TableCell style={{ fontWeight: '500' }} rowSpan={record.parametersData?.length || 1}>
                    {record.no}
                  </TableCell>
                  <TableCell rowSpan={record.parametersData?.length || 1}>{record.templateName}</TableCell>
                  <TableCell rowSpan={record.parametersData?.length || 1}>{record.createdBy}</TableCell>
                  <TableCell rowSpan={record.parametersData?.length || 1}>
                    {record.tags.map((tag, index) => (
                      <div key={index}>• {tag}</div>
                    ))}
                  </TableCell>
                  <TableCell rowSpan={record.parametersData?.length || 1}>{record.gender}</TableCell>
                  <TableCell rowSpan={record.parametersData?.length || 1}>{record.ageRange}</TableCell>
                  <TableCell>
                    {record.parametersData?.[0]?.parameter_name || '-'}
                  </TableCell>
                  <TableCell style={{ padding: 0 }}>
                    {(() => {
                      const param = record.parametersData?.[0];
                      if (!param) return '-';
                      const allNormalRanges = param.all_normal_ranges || [];

                      if (allNormalRanges.length > 0) {
                        return (
                          <div style={{ padding: '0.5rem' }}>
                            <SubTable>
                              <thead>
                                <tr>
                                  <SubTableHeaderCell>เพศ</SubTableHeaderCell>
                                  <SubTableHeaderCell>อายุ</SubTableHeaderCell>
                                  <SubTableHeaderCell>ค่าปกติ</SubTableHeaderCell>
                                </tr>
                              </thead>
                              <tbody>
                                {allNormalRanges.map((range, idx) => (
                                  <SubTableRow key={idx}>
                                    <SubTableCell>{range.sex}</SubTableCell>
                                    <SubTableCell>{range.age_range}</SubTableCell>
                                    <SubTableCell>{range.normal_range}</SubTableCell>
                                  </SubTableRow>
                                ))}
                              </tbody>
                            </SubTable>
                          </div>
                        );
                      } else {
                        return <div style={{ padding: '0.5rem' }}>{param.normal_range || '-'}</div>;
                      }
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const param = record.parametersData?.[0];
                      if (!param) return '-';
                      return <div style={{ padding: '0.5rem' }}>{renderCustomValue(param)}</div>;
                    })()}
                  </TableCell>
                  <TableCell rowSpan={record.parametersData?.length || 1}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                      <EditButton
                        onClick={async () => {
                          try {
                            const response = await templateAPI.getById(record.id);

                            if (response.status === 'success' && response.data) {
                              const template = response.data;

                              const editData = {
                                templateName: template.template_name,
                                createdBy: template.created_by,
                                tags: template.tags || [],
                                gender: template.gender,
                                ageRange: [
                                  template.age_min !== null ? template.age_min : '',
                                  template.age_max !== null ? template.age_max : ''
                                ],
                                selectedParameters: template.parameters?.map(p => ({
                                  name: p.parameter_name,
                                  category: p.parameter_category,
                                  unit: p.unit || '',
                                  normalValue: p.normal_range || '-',
                                  normalRange: p.normal_range ? [0, 100] : null,
                                  defaultRange: [p.custom_min, p.custom_max],
                                  allNormalRanges: p.all_normal_ranges || []
                                })) || [],
                                parameterRanges: template.parameters?.reduce((acc, p) => {
                                  if (!isRadioParameter(p.parameter_name)) {
                                    acc[p.parameter_name] = [p.custom_min, p.custom_max];
                                  }
                                  return acc;
                                }, {}) || {},
                                parameterRadioValues: template.parameters?.reduce((acc, p) => {
                                  if (isRadioParameter(p.parameter_name) && p.custom_value) {
                                    acc[p.parameter_name] = p.custom_value;
                                  }
                                  return acc;
                                }, {}) || {}
                              };

                              setEditingTemplate({ id: record.id, data: editData });
                              setIsEditModalOpen(true);
                            }
                          } catch (error) {
                            console.error('❌ Error loading template:', error);
                          }
                        }}
                        title="แก้ไข"
                      >
                        แก้ไข
                      </EditButton>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <ToggleSwitch
                          active={record.isActive}
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            try {
                              const response = await templateAPI.toggleActive(record.id);

                              if (response.status === 'success') {
                                await loadTemplates();
                                await loadActivityLogs();
                              } else {
                                console.error('❌ API returned error:', response);
                                alert('เกิดข้อผิดพลาด: ' + (response.message || 'Unknown error'));
                              }
                            } catch (error) {
                              console.error('❌ Error toggling template:', error);
                              alert('ไม่สามารถเปลี่ยนสถานะได้: ' + error.message);
                            }
                          }}
                        >
                          <ToggleCircle active={record.isActive} />
                        </ToggleSwitch>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
                {record.parametersData?.slice(1).map((param, paramIndex) => {
                  const allNormalRanges = param.all_normal_ranges || [];

                  return (
                    <TableRow key={`${record.id}-param-${paramIndex + 1}`} data-sub-row="true">
                      <TableCell>{param.parameter_name}</TableCell>
                      <TableCell style={{ padding: 0 }}>
                        {allNormalRanges.length > 0 ? (
                          <div style={{ padding: '0.5rem' }}>
                            <SubTable>
                              <thead>
                                <tr>
                                  <SubTableHeaderCell>เพศ</SubTableHeaderCell>
                                  <SubTableHeaderCell>อายุ</SubTableHeaderCell>
                                  <SubTableHeaderCell>ค่าปกติ</SubTableHeaderCell>
                                </tr>
                              </thead>
                              <tbody>
                                {allNormalRanges.map((range, idx) => (
                                  <SubTableRow key={idx}>
                                    <SubTableCell>{range.sex}</SubTableCell>
                                    <SubTableCell>{range.age_range}</SubTableCell>
                                    <SubTableCell>{range.normal_range}</SubTableCell>
                                  </SubTableRow>
                                ))}
                              </tbody>
                            </SubTable>
                          </div>
                        ) : (
                          <div style={{ padding: '0.5rem' }}>{param.normal_range || '-'}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div style={{ padding: '0.5rem' }}>{renderCustomValue(param)}</div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                </React.Fragment>
                ))
              )}
            </tbody>
          </Table>
        </TableContainer>
      </PageContainer>

      <PageContainer>
        <PageTitle>Activity Log</PageTitle>
        
        <TableContainer>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>วันที่</TableHeaderCell>
                <TableHeaderCell>เวลา</TableHeaderCell>
                <TableHeaderCell>Template</TableHeaderCell>
                <TableHeaderCell>สร้างโดย</TableHeaderCell>
                <TableHeaderCell>Event</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <tbody>
              {activityLog.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
                    ไม่มี Activity Log
                  </TableCell>
                </TableRow>
              ) : (
                currentActivityLogs.map((log, index) => (
                  <TableRow key={index}>
                    <TableCell>{log.date}</TableCell>
                    <TableCell>{log.time}</TableCell>
                    <TableCell style={{ fontWeight: '500' }}>
                      {log.template}
                    </TableCell>
                    <TableCell>{log.createdBy}</TableCell>
                    <TableCell>
                      {log.event}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </TableContainer>

        {activityLog.length > 0 && (
          <PaginationContainer>
            <PaginationInfo>
              แสดง {startIndex + 1}-{Math.min(endIndex, activityLog.length)} จาก {activityLog.length} รายการ
            </PaginationInfo>
            <PaginationControls>
              <PageButton
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ก่อนหน้า
              </PageButton>

              {getPageNumbers().map((page, index) => (
                page === '...' ? (
                  <PageEllipsis key={`ellipsis-${index}`}>...</PageEllipsis>
                ) : (
                  <PageButton
                    key={page}
                    active={currentPage === page}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </PageButton>
                )
              ))}

              <PageButton
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                ถัดไป
              </PageButton>
            </PaginationControls>
          </PaginationContainer>
        )}
      </PageContainer>

      <CreateTemplateModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveTemplate}
      />

      <EditTemplateModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleEditTemplate}
        templateData={editingTemplate?.data}
      />
    </Layout>
  );
};

export default HealthBook;
